"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, FileText, CloudDownload as DownloadCloud, Trash2, CircleCheck as CheckCircle2, Clock, Mail, ArrowRight, CircleQuestionMark as HelpCircle, KeyRound, FileCheck2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const legalFrameworks = [{
  region: "European Union & UK",
  law: "GDPR / UK GDPR",
  scope: "Covers all European and UK subscribers, readers, and research participants."
}, {
  region: "United States (California)",
  law: "CCPA / CPRA",
  scope: "Protects California residents regarding personal data disclosure and opt-out rights."
}, {
  region: "Brazil",
  law: "LGPD",
  scope: "Safeguards Brazilian readers and institutional research collaborators."
}];
const rightsItems = [{
  id: "right-access",
  title: "Right to access and transparency",
  frameworks: "GDPR Art. 15 · CCPA § 1798.100 · LGPD Art. 18",
  description: "You have the right to request confirmation on whether we process your personal data and to receive a clear copy of the specific categories of data MinfoMedia maintains regarding your account, downloads, and reading preferences.",
  details: "Upon verification, we will provide a comprehensive record detailing data sources, processing purposes, recipients, and third-party data handlers involved in delivering our editorial services."
}, {
  id: "right-rectification",
  title: "Right to rectification and correction",
  frameworks: "GDPR Art. 16 · LGPD Art. 18",
  description: "If any information we hold about you is inaccurate, outdated, or incomplete—such as your professional affiliation, job title, or delivery email—you may request immediate correction without undue delay.",
  details: "You can update routine newsletter settings directly via your email preference center or submit an editorial record adjustment request through our privacy desk."
}, {
  id: "right-erasure",
  title: "Right to erasure (right to be forgotten)",
  frameworks: "GDPR Art. 17 · CCPA § 1798.105 · LGPD Art. 18",
  description: "You may ask us to permanently delete your personal information when it is no longer necessary for the original publication or transaction purposes, or when you withdraw your consent.",
  details: "We will erase your records across our active subscriber systems and instruct our third-party infrastructure providers to do the same, preserving only legally required financial audit receipts."
}, {
  id: "right-portability",
  title: "Right to data portability",
  frameworks: "GDPR Art. 20 · LGPD Art. 18",
  description: "You are entitled to receive your personal data in a structured, commonly used, and machine-readable format (such as JSON or CSV), and have that data transferred directly to another organization where technically feasible.",
  details: "This includes your download archive history, registered webinar attendance logs, and self-submitted survey or commentary records."
}, {
  id: "right-optout",
  title: "Right to opt out and restrict processing",
  frameworks: "GDPR Art. 18 & 21 · CCPA § 1798.120",
  description: "We do not sell personal data for monetary gain. Under CCPA/CPRA, you may explicitly opt out of any potential data sharing for cross-context behavioral advertising and restrict automated analytics profiling.",
  details: "You can adjust cookie settings anytime using our dedicated privacy footer controls or transmit a Global Privacy Control (GPC) signal through your browser."
}, {
  id: "right-nondiscrimination",
  title: "Right to non-discrimination",
  frameworks: "CCPA § 1798.125",
  description: "Exercising your privacy rights will never result in denied access to published research reports, degraded editorial services, or disparate pricing for research materials.",
  details: "All freely published business analysis, articles, and whitepapers remain accessible under identical editorial conditions regardless of your privacy elections."
}];
const dsarSteps = [{
  step: "01",
  title: "Submit your request",
  description: "Send an email to our dedicated data privacy desk at info@minfomedia.com stating your request type (access, deletion, portability, or correction)."
}, {
  step: "02",
  title: "Identity confirmation",
  description: "To safeguard your personal data from unauthorized access, we verify your email ownership through a secure confirmation link or two-factor prompt."
}, {
  step: "03",
  title: "Fulfillment & report delivery",
  description: "Our privacy officer processes your file and sends a complete, encrypted summary within 30 days, free of charge."
}];
export default function PrivacyRights() {
  const shouldReduceMotion = useReducedMotion();
  const [activeAccordion, setActiveAccordion] = useState<string>("right-access");
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="your-privacy-rights-data-requests" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border selection:bg-muted">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        
        {/* Section Header */}
        <div className="max-w-[840px] mx-auto text-center mb-14 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-muted border border-border">
            <ShieldCheck className="size-3.5 text-foreground" />
            <span className="text-[11px] sm:text-xs font-mono font-medium tracking-wide uppercase text-muted-foreground">
              Institutional Compliance
            </span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground mb-4">
            Your privacy rights & data requests
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Review how you can inspect, export, correct, or delete the data we hold across our research and publishing platforms.
          </p>

          {/* Masthead Rule Motif */}
          <div className="mt-8 flex flex-col items-center justify-center gap-1">
            <div className="w-24 h-[1px] bg-foreground/70" />
            <div className="w-12 h-[0.5px] bg-foreground/40" />
          </div>
        </div>

        {/* Legal Frameworks Banner */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeInUp} className="mb-14">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {legalFrameworks.map((framework, i) => <div key={framework.law} data-index={i} className="bg-card border border-border rounded-md p-5 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-foreground/30 hover:shadow-sm">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-xs font-mono font-semibold uppercase text-muted-foreground tracking-wider">
                    {framework.region}
                  </span>
                  <span className="inline-block px-2 py-0.5 rounded-sm bg-muted text-[11px] font-mono font-medium text-foreground border border-border">
                    {framework.law}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground leading-normal">
                  {framework.scope}
                </p>
              </div>)}
          </div>
        </motion.div>

        {/* Main Content Layout: Rights Accordion & DSAR Side Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Left Column: Interactive Rights Breakdown (Accordion) */}
          <div className="lg:col-span-7">
            <div className="border-b border-border pb-3 mb-6">
              <h3 className="text-xl sm:text-2xl font-serif font-semibold text-foreground">
                Recognized statutory rights
              </h3>
              <p className="text-sm text-muted-foreground mt-1 font-sans">
                Select any right below to read the statutory scope and our operational obligations.
              </p>
            </div>

            <Accordion type="single" collapsible value={activeAccordion} onValueChange={val => setActiveAccordion(val || "")} className="w-full space-y-3">
              {rightsItems.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="bg-card border border-border rounded-md px-5 py-1 shadow-2xs transition-[border-color] duration-200 hover:border-foreground/30 last:border-b">
                  <AccordionTrigger className="text-left font-serif font-semibold text-base sm:text-lg text-foreground py-4 hover:no-underline [&[data-state=open]]:text-foreground">
                    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1.5 sm:gap-3 text-left">
                      <span>{item.title}</span>
                      <span className="text-xs font-mono font-normal text-muted-foreground tracking-normal">
                        {item.frameworks}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-1 pb-5 text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
                    <p className="mb-3 text-foreground/90">
                      {item.description}
                    </p>
                    <div className="p-3.5 rounded-sm bg-muted/60 border border-border text-xs sm:text-sm text-muted-foreground flex items-start gap-2.5">
                      <CheckCircle2 className="size-4 shrink-0 text-foreground mt-0.5" />
                      <span>{item.details}</span>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {/* Right Column: DSAR Protocol & Direct Request Panel */}
          <div className="lg:col-span-5 space-y-6">
            <Card className="border border-border bg-card shadow-sm rounded-md overflow-hidden">
              <CardContent className="p-6 sm:p-7">
                <div className="flex items-center gap-2.5 text-foreground mb-3">
                  <FileText className="size-5" />
                  <h3 className="text-lg sm:text-xl font-serif font-semibold">
                    Submit a data subject request (DSAR)
                  </h3>
                </div>
                
                <p className="text-sm text-muted-foreground font-sans leading-relaxed mb-6">
                  You can formally exercise any statutory right under GDPR, CCPA, or LGPD without fees. We fulfill verified inquiries in accordance with statutory timelines.
                </p>

                {/* 3-Step DSAR Workflow */}
                <div className="space-y-4 mb-7">
                  {dsarSteps.map((dsar, i) => <div key={dsar.step} data-index={i} className="flex items-start gap-3.5">
                      <div className="size-7 rounded-sm bg-muted border border-border flex items-center justify-center font-mono text-xs font-semibold text-foreground shrink-0 mt-0.5">
                        {dsar.step}
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold font-sans text-foreground">
                          {dsar.title}
                        </h4>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-normal font-sans">
                          {dsar.description}
                        </p>
                      </div>
                    </div>)}
                </div>

                {/* Processing Specifications */}
                <div className="p-4 rounded-sm bg-muted/40 border border-border mb-6 space-y-2 text-xs text-muted-foreground font-mono">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <Clock className="size-3.5 text-foreground" />
                      Standard response window
                    </span>
                    <span className="font-semibold text-foreground">Within 30 days</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-border/60 pt-2">
                    <span className="flex items-center gap-1.5">
                      <KeyRound className="size-3.5 text-foreground" />
                      Verification requirement
                    </span>
                    <span className="font-semibold text-foreground">Email authentication</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-border/60 pt-2">
                    <span className="flex items-center gap-1.5">
                      <DownloadCloud className="size-3.5 text-foreground" />
                      Export file formats
                    </span>
                    <span className="font-semibold text-foreground">JSON / CSV</span>
                  </div>
                </div>

                {/* Direct Action Buttons */}
                <div className="space-y-3">
                  <Button asChild className="w-full h-11 bg-primary text-primary-foreground font-medium rounded-sm shadow-xs hover:bg-primary/90 transition-colors">
                    <a href="mailto:info@minfomedia.com?subject=Data%20Subject%20Access%20Request%20(DSAR)">
                      <Mail className="size-4 mr-2" />
                      Initiate DSAR via privacy desk
                    </a>
                  </Button>

                  <Button asChild variant="outline" className="w-full h-10 border-border text-foreground font-medium rounded-sm hover:bg-muted transition-colors text-xs">
                    <Link to="/TermsConditionsPage">
                      <FileCheck2 className="size-3.5 mr-1.5" />
                      Read full terms & conditions
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Editorial Privacy Assurance Note */}
            <div className="p-5 rounded-md bg-card border border-border shadow-2xs">
              <div className="flex items-start gap-3">
                <HelpCircle className="size-4 text-muted-foreground shrink-0 mt-0.5" />
                <div className="text-xs text-muted-foreground leading-relaxed">
                  <span className="font-semibold text-foreground block font-sans mb-0.5">
                    Questions about your research subscriptions?
                  </span>
                  For routine unsubscribe actions or report notification settings, click the one-click preference link found in the footer of any MinfoMedia newsletter.
                </div>
              </div>
            </div>

          </div>

        </div>

      </div>
    </section>;
}